import axios from 'axios'

export type ApiErrorKind =
  | 'authentication'
  | 'authorization'
  | 'validation'
  | 'not-found'
  | 'conflict'
  | 'network'
  | 'server'
  | 'unknown'

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status: number | null
  readonly code: string | null
  readonly correlationId: string | null
  readonly retryable: boolean

  constructor(input: {
    message: string
    kind: ApiErrorKind
    status?: number | null
    code?: string | null
    correlationId?: string | null
    retryable?: boolean
  }) {
    super(input.message)
    this.name = 'ApiError'
    this.kind = input.kind
    this.status = input.status ?? null
    this.code = input.code ?? null
    this.correlationId = input.correlationId ?? null
    this.retryable = input.retryable ?? false
  }
}

function kindFromStatus(status: number | null): ApiErrorKind {
  if (status === 401) return 'authentication'
  if (status === 403) return 'authorization'
  if (status === 404) return 'not-found'
  if (status === 409) return 'conflict'
  if (status === 400 || status === 422) return 'validation'
  if (status !== null && status >= 500) return 'server'
  return status === null ? 'network' : 'unknown'
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error
  if (!axios.isAxiosError(error)) {
    return new ApiError({
      message: error instanceof Error ? error.message : 'Unexpected error',
      kind: 'unknown',
    })
  }

  const status = error.response?.status ?? null
  const payload = error.response?.data as
    { message?: unknown; error?: unknown; code?: unknown } | undefined
  const nestedError =
    payload?.error && typeof payload.error === 'object'
      ? (payload.error as Record<string, unknown>)
      : null
  const apiMessage =
    (typeof payload?.message === 'string' && payload.message) ||
    (typeof nestedError?.message === 'string' && nestedError.message) ||
    (typeof payload?.error === 'string' && payload.error) ||
    error.message
  const apiCode =
    (typeof payload?.code === 'string' && payload.code) ||
    (typeof nestedError?.code === 'string' && nestedError.code) ||
    null
  const correlationId =
    error.response?.headers?.['x-correlation-id'] ??
    error.config?.headers?.['X-Correlation-Id'] ??
    null

  return new ApiError({
    message: apiMessage,
    kind: kindFromStatus(status),
    status,
    code: apiCode,
    correlationId: String(correlationId || '') || null,
    retryable:
      status === null || status === 429 || (status >= 500 && status < 600),
  })
}
